import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ==================== STYLES ====================
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '20px auto',
    padding: '20px',
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  questionHeader: {
    marginBottom: '30px',
  },
  title: {
    color: '#1976d2',
    fontSize: '24px',
    marginBottom: '10px',
  },
  questionText: {
    fontSize: '18px',
    color: '#333',
    margin: '15px 0',
    lineHeight: '1.6',
  },
  instruction: {
    background: '#e3f2fd',
    padding: '12px 15px',
    borderRadius: '8px',
    color: '#1565c0',
    fontSize: '14px',
    borderLeft: '4px solid #1976d2',
  },
  matchingContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 60px 1fr',
    gap: '20px',
    alignItems: 'start',
    margin: '30px 0',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  columnHeader: {
    fontWeight: 600,
    color: '#1976d2',
    padding: '12px 15px',
    background: '#f5f5f5',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  leftItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '15px',
    background: '#f8f9fa',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    minHeight: '60px',
    transition: 'all 0.3s ease',
  },
  leftItemCorrect: {
    background: '#e8f5e9',
    borderColor: '#4caf50',
    boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)',
  },
  itemNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    background: '#1976d2',
    color: 'white',
    borderRadius: '50%',
    fontWeight: 600,
    fontSize: '14px',
    flexShrink: 0,
  },
  itemContent: {
    flex: 1,
    fontSize: '15px',
    color: '#333',
    lineHeight: '1.4',
  },
  connectionLine: {
    position: 'relative',
    width: '2px',
    background: '#1976d2',
    alignSelf: 'stretch',
    margin: '40px 0',
  },
  draggableItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '15px',
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    minHeight: '60px',
    cursor: 'grab',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  draggableItemHover: {
    borderColor: '#1976d2',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
    transform: 'translateY(-2px)',
  },
  draggableItemMatched: {
    background: '#e8f5e9',
    borderColor: '#4caf50',
    boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)',
  },
  dragHandle: {
    color: '#999',
    fontSize: '20px',
    cursor: 'grab',
    userSelect: 'none',
    padding: '0 5px',
  },
  scoreDisplay: {
    marginTop: '30px',
    padding: '20px',
    background: '#f5f5f5',
    borderRadius: '8px',
    borderLeft: '4px solid #1976d2',
  },
  scoreTitle: {
    color: '#1976d2',
    marginBottom: '10px',
    fontSize: '18px',
  },
  scoreText: {
    color: '#555',
    marginBottom: '15px',
    fontSize: '16px',
  },
  progressBar: {
    width: '100%',
    height: '30px',
    background: '#e0e0e0',
    borderRadius: '15px',
    overflow: 'hidden',
    position: 'relative',
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.6s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    borderRadius: '15px',
  },
  resetButton: {
    marginTop: '20px',
    padding: '12px 24px',
    background: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
};

// ==================== COMPOSANT DRAGGABLE ====================
const DraggableItem = ({ id, content, isMatched }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    ...styles.draggableItem,
    ...(isMatched ? styles.draggableItemMatched : {}),
    ...(isHovered && !isDragging ? styles.draggableItemHover : {}),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.dragHandle}>⋮⋮</div>
      <div style={styles.itemContent}>{content}</div>
    </div>
  );
};

// ==================== COMPOSANT PRINCIPAL ====================
const DragDropMatching = ({
  questionText,
  matchingPairs,
  onAnswerChange,
  showCorrectAnswers = false,
  isReadOnly = false,
}) => {
  // États
  const [leftItems] = useState(
    matchingPairs.map((pair, index) => ({
      id: `left-${index}`,
      content: pair.leftItem,
      originalIndex: index,
    }))
  );

  const [rightItems, setRightItems] = useState(() =>
    matchingPairs
      .map((pair, index) => ({
        id: `right-${index}`,
        content: pair.rightItem,
        originalIndex: index,
      }))
      .sort(() => Math.random() - 0.5) // Mélanger
  );

  const [activeId, setActiveId] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState(new Set());

  // Configuration des sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Vérifier les correspondances
  useEffect(() => {
    const matched = new Set();
    rightItems.forEach((item, index) => {
      if (item.originalIndex === index) {
        matched.add(item.id);
      }
    });
    setMatchedPairs(matched);

    // Notifier le parent
    if (onAnswerChange) {
      const userAnswers = rightItems.map((item, index) => ({
        leftId: index,
        rightId: item.originalIndex,
      }));
      onAnswerChange(userAnswers);
    }
  }, [rightItems, onAnswerChange]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setRightItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  };

  // Calculer le score
  const calculateScore = () => {
    let correct = 0;
    rightItems.forEach((item, index) => {
      if (item.originalIndex === index) {
        correct++;
      }
    });
    return {
      correct,
      total: matchingPairs.length,
      percentage: (correct / matchingPairs.length) * 100,
    };
  };

  const score = calculateScore();
  const activeItem = rightItems.find((item) => item.id === activeId);

  return (
    <div style={styles.container}>
      <div style={styles.questionHeader}>
        <h3 style={styles.title}>Question de correspondance</h3>
        <p style={styles.questionText}>{questionText}</p>
        <p style={styles.instruction}>
          📌 Glissez les éléments de droite pour les réordonner et faire correspondre
        </p>
      </div>

      <div style={styles.matchingContainer}>
        {/* Colonne gauche */}
        <div style={styles.column}>
          <div style={styles.columnHeader}>Éléments</div>
          {leftItems.map((item, index) => (
            <div
              key={item.id}
              style={{
                ...styles.leftItem,
                ...(showCorrectAnswers && matchedPairs.has(rightItems[index]?.id)
                  ? styles.leftItemCorrect
                  : {}),
              }}
            >
              <span style={styles.itemNumber}>{index + 1}</span>
              <span style={styles.itemContent}>{item.content}</span>
            </div>
          ))}
        </div>

        {/* Ligne de connexion */}
        <div style={styles.connectionLine}></div>

        {/* Colonne droite */}
        <div style={styles.column}>
          <div style={styles.columnHeader}>À réorganiser</div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => setActiveId(e.active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext
              items={rightItems.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {rightItems.map((item) => (
                <DraggableItem
                  key={item.id}
                  id={item.id}
                  content={item.content}
                  isMatched={showCorrectAnswers && matchedPairs.has(item.id)}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeItem ? (
                <div style={{ ...styles.draggableItem, cursor: 'grabbing', opacity: 0.9 }}>
                  <div style={styles.dragHandle}>⋮⋮</div>
                  <div style={styles.itemContent}>{activeItem.content}</div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {/* Score */}
      {showCorrectAnswers && (
        <div style={styles.scoreDisplay}>
          <h4 style={styles.scoreTitle}>Résultats</h4>
          <p style={styles.scoreText}>
            Réponses correctes: <strong>{score.correct}</strong> / {score.total} (
            {score.percentage.toFixed(0)}%)
          </p>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${score.percentage}%`,
                backgroundColor:
                  score.percentage === 100
                    ? '#4caf50'
                    : score.percentage >= 50
                    ? '#ff9800'
                    : '#f44336',
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Bouton reset */}
      {!isReadOnly && (
        <button
          style={styles.resetButton}
          onClick={() => setRightItems([...rightItems].sort(() => Math.random() - 0.5))}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1565c0';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#1976d2';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          🔄 Mélanger à nouveau
        </button>
      )}
    </div>
  );
};

export default DragDropMatching;